import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import { EmptyState } from '@/components/EmptyState';
import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import { useShowToast } from '@/design_system/Toast';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconEdit from '@/icons/Edit.svg';
import IconExternalLink from '@/icons/ExternalLink.svg';
import IconTrash from '@/icons/Trash.svg';
import {
  useCreateSparePartType,
  useDeleteSparePartType,
  useSparePartTypesOrganization,
  useUpdateSparePartType,
} from '@/models/sparePartType';
import { I18nWording, SUPPORTED_LOCALES, useDatabaseTranslation } from '@/services/i18n';
import { isMissingTranslation } from '@/utils/isMissingTranslation';
import { trimI18nWording } from '@/utils/trimI18nWording';
import useViewPort from '@/utils/useViewport';

import { AgreementPricesDialog } from './shared/AgreementPricesDialog';
import { OptionalLocaleInputs } from './shared/LocaleInputs';
import { NameInputs } from './shared/NameInputs';
import { ProductAttributeValuesHierarchy } from './shared/ProductAttributeValuesHierarchy';
import { ProductAttributeValuesMultiSelects } from './shared/ProductAttributeValuesMultiSelects';

type SparePartType = Endpoints['GET /spare-part-types/organization']['response'][number];

export const SparePartTypes = () => {
  const { _compare } = useDatabaseTranslation();

  const { data: sparePartTypes, isLoading } = useSparePartTypesOrganization();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [sparePartTypeToEdit, setSparePartTypeToEdit] = useState<SparePartType | null>(null);

  const columnWidths = {
    name: 'minmax(300px, auto)',
    productCategories: 'minmax(300px, auto)',
    details: 'auto',
    buttons: '120px',
  };

  const sortedSparePartTypes = sparePartTypes?.toSorted((a, b) => _compare(a.name, b.name));

  return (
    <Stack gap="1.5rem" padding="0 0 24px 0">
      <Stack row alignItems="center" justifyContent="space-between" gap="8px">
        <Title size="200" as="h2">
          <Trans id="settings.spare-parts.title">Spare parts</Trans>
        </Title>
        <Button onPress={() => setIsDrawerOpen(true)}>
          <IconAdd />
          <Trans id="settings.spare-parts.add">New spare part</Trans>
        </Button>
      </Stack>
      {!isLoading && !sortedSparePartTypes?.length ? (
        <EmptyState subtitle={<Trans id="settings.spare-parts.none">No spare parts</Trans>} />
      ) : (
        <Table
          ariaLabel={t({ id: 'settings.spare-parts.table.label', message: 'Spare parts' })}
          columnWidths={[
            columnWidths.name,
            columnWidths.productCategories,
            columnWidths.details,
            columnWidths.buttons,
          ]}
          variant="grid"
          bordered
        >
          <Header>
            <Row>
              <Column>
                <Trans id="settings.spare-parts.column.name">Name</Trans>
              </Column>
              <Column>
                <Trans id="settings.spare-parts.column.product-categories">
                  Product categories
                </Trans>
              </Column>
              <Column>
                <Trans id="settings.spare-parts.column.details">Details</Trans>
              </Column>
              <Column
                ariaLabel={t({
                  id: 'settings.spare-parts.column.buttons',
                  message: 'View prices, edit or delete',
                })}
              />
            </Row>
          </Header>
          <Body>
            {sortedSparePartTypes?.map((sparePartType) => (
              <SparePartTypeRow
                key={sparePartType.id}
                sparePartType={sparePartType}
                onEdit={() => {
                  setSparePartTypeToEdit(sparePartType);
                  setIsDrawerOpen(true);
                }}
              />
            ))}
          </Body>
        </Table>
      )}
      <SparePartDrawer
        sparePartType={sparePartTypeToEdit}
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);

          // Wait for the Drawer CSS exit animation to finish
          setTimeout(() => {
            setSparePartTypeToEdit(null);
          }, 150);
        }}
      />
    </Stack>
  );
};

const SparePartTypeRow = ({
  sparePartType,
  onEdit,
}: {
  sparePartType: SparePartType;
  onEdit: () => void;
}) => {
  const showToast = useShowToast();
  const { _db } = useDatabaseTranslation();

  const [deleteConfirmationOpened, setDeleteConfirmationOpened] = useState(false);
  const { mutateAsync: deleteSparePartType, isPending: isDeleteSparePartTypePending } =
    useDeleteSparePartType();

  const sparePartTypeName = _db(sparePartType.name);

  return (
    <Row>
      <Cell>{_db(sparePartType.name)}</Cell>
      <Cell>
        <ProductAttributeValuesHierarchy
          productAttributeValues={sparePartType.productAttributeValues}
        />
      </Cell>
      <Cell>
        <Stack>
          <span>
            <InlineText size="200" weight="medium">
              <Trans id="settings.spare-parts.unit">Unit</Trans>
              <Trans id="_general.colon">:</Trans>{' '}
            </InlineText>
            <InlineText size="200" color="secondary">
              {sparePartType.unit ? _db(sparePartType.unit) : '-'}
            </InlineText>
          </span>
          <span>
            <Stack row gap="0.25rem" alignItems="center">
              <InlineText size="200" weight="medium">
                <Trans id="settings.spare-parts.image">Image</Trans>
                <Trans id="_general.colon">:</Trans>{' '}
              </InlineText>
              <InlineText size="200" color="secondary">
                {sparePartType.imageUrl ? (
                  <a target="_blank" href={sparePartType.imageUrl} rel="noreferrer">
                    <IconExternalLink />
                  </a>
                ) : (
                  '-'
                )}
              </InlineText>
            </Stack>
          </span>
        </Stack>
      </Cell>
      <Cell justifyContent="center">
        <Stack row gap="0.5rem" flexWrap="nowrap">
          <Button
            variant="secondary"
            iconOnly
            size="small"
            ariaLabel={t({ id: 'settings.spare-parts.edit', message: 'Edit' })}
            onPress={onEdit}
          >
            <IconEdit />
          </Button>
          <AgreementPricesDialog
            id={sparePartType.id}
            name={_db(sparePartType.name)}
            agreementPrices={sparePartType.agreementPrices}
            type="spare-part"
            mode="brand"
          />
          <Button
            variant="danger"
            iconOnly
            size="small"
            ariaLabel={t({ id: 'settings.spare-parts.delete', message: 'Delete' })}
            onPress={() => setDeleteConfirmationOpened(true)}
          >
            <IconTrash />
          </Button>
          <ConfirmationDialog
            title={
              <Trans id="settings.spare-parts.delete.confirmation.title">Delete spare part</Trans>
            }
            confirm={<Trans id="settings.spare-parts.delete.confirmation.confirm">Delete</Trans>}
            onConfirm={() =>
              deleteSparePartType(sparePartType.id)
                .then(() => {
                  showToast({
                    text: (
                      <Trans id="settings.spare-parts.delete.confirmation.success">
                        The spare part <b>&quot;{sparePartTypeName}&quot;</b> has been deleted.
                      </Trans>
                    ),
                    type: 'success',
                  });
                })
                .catch(() => {
                  showToast({
                    text: (
                      <Trans id="settings.spare-parts.delete.confirmation.error">
                        Error while deleting the spare part <b>&quot;{sparePartTypeName}&quot;</b>.
                        Please try again later.
                      </Trans>
                    ),
                    type: 'error',
                  });
                })
            }
            showDialog={deleteConfirmationOpened}
            setShowDialog={setDeleteConfirmationOpened}
            maxWidth="35rem"
            isPending={isDeleteSparePartTypePending}
          >
            <Stack gap="0.5rem">
              <p>
                <Trans id="settings.spare-parts.delete.confirmation.description.1">
                  Are you sure you want to delete the spare part{' '}
                  <b>&quot;{sparePartTypeName}&quot;</b>?
                </Trans>
              </p>
              <p>
                <Trans id="settings.spare-parts.delete.confirmation.description.2">
                  Active requests with this spare part will continue to show it, but users won't be
                  able to choose this spare part for new requests.
                </Trans>
              </p>
              <hr />
              <p>
                <b>
                  <Trans id="settings.spare-parts.delete.confirmation.description.3">
                    This deletion cannot be undone.
                  </Trans>
                </b>
              </p>
            </Stack>
          </ConfirmationDialog>
        </Stack>
      </Cell>
    </Row>
  );
};

const SparePartDrawer = ({
  sparePartType,
  onClose,
  isOpen,
}: {
  sparePartType: SparePartType | null;
  onClose: () => void;
  isOpen: boolean;
}) => {
  return (
    <Drawer isOpen={isOpen} onOpenChange={onClose}>
      <SparePartDrawerContent sparePartType={sparePartType} onClose={onClose} />
    </Drawer>
  );
};

const SparePartDrawerContent = ({
  sparePartType,
  onClose,
}: {
  sparePartType: SparePartType | null;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const showToast = useShowToast();
  const { _db } = useDatabaseTranslation();

  const { mutateAsync: updateSparePartType, isPending: isUpdateSparePartTypePending } =
    useUpdateSparePartType();
  const { mutateAsync: createSparePartType, isPending: isCreateSparePartTypePending } =
    useCreateSparePartType();

  const [name, setName] = useState(
    Object.fromEntries(
      SUPPORTED_LOCALES.map(({ id }) => [id, sparePartType?.name[id] ?? ''])
    ) as I18nWording
  );
  const [unit, setUnit] = useState(sparePartType?.unit ?? null);
  const [imageUrl, setImageUrl] = useState(sparePartType?.imageUrl ?? '');
  const [productAttributeValues, setProductAttributeValues] = useState(
    sparePartType?.productAttributeValues ?? []
  );

  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const nameError = isMissingTranslation(name);
  const unitError = !!unit && isMissingTranslation(unit);
  const hasError = nameError || unitError;

  const onSave = async () => {
    setShowErrors(true);

    if (hasError) {
      return;
    }

    try {
      const body = {
        name: trimI18nWording(name),
        unit: unit ? trimI18nWording(unit) : unit,
        imageUrl,
        productAttributeValues: productAttributeValues.map(({ id }) => id),
      };

      if (sparePartType) {
        await updateSparePartType({
          sparePartTypeId: sparePartType.id,
          ...body,
        });
      } else {
        await createSparePartType({
          ...body,
          agreementPrices: [],
        });
      }

      onClose();

      const sparePartTypeName = _db(name);

      showToast({
        text: (
          <Trans id="settings.spare-parts.drawer.update-success">
            Defect &quot;{sparePartTypeName}&quot; successfully saved
          </Trans>
        ),
      });
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <DrawerHeader>
        <Title size="300">
          {sparePartType ? (
            <Trans id="settings.spare-parts.drawer.title">Update spare part</Trans>
          ) : (
            <Trans id="settings.spare-parts.drawer.title-create">Create spare part</Trans>
          )}
        </Title>
      </DrawerHeader>
      <DrawerBody>
        <Stack padding={isMobile ? '1rem' : '2rem'} gap="1rem">
          <NameInputs name={name} setName={setName} showErrors={showErrors} />
          <OptionalLocaleInputs
            label={t({ id: 'settings.spare-parts.drawer.unit', message: 'Unit' })}
            placeholder={t({ id: 'settings.spare-parts.drawer.unit', message: 'Unit' })}
            value={unit}
            setValue={setUnit}
            showErrors={showErrors}
          />
          <InputText
            label={t({ id: 'settings.spare-parts.drawer.image', message: 'Image URL' })}
            placeholder={t({ id: 'settings.spare-parts.drawer.image', message: 'Image URL' })}
            value={imageUrl}
            onChange={setImageUrl}
          />
          <ProductAttributeValuesMultiSelects
            productAttributeValues={productAttributeValues}
            setProductAttributeValues={setProductAttributeValues}
          />
        </Stack>
      </DrawerBody>
      <Footer>
        {(apiError || (showErrors && hasError)) && (
          <FooterError>
            {!apiError && showErrors && hasError ? (
              <Trans id="settings.spare-parts.drawer.error">
                Please fill the missing information
              </Trans>
            ) : (
              apiError
            )}
          </FooterError>
        )}
        <FooterButtons>
          <Button variant="secondary" size="medium" onPress={onClose}>
            <Trans id="settings.spare-parts.drawer.cancel">Cancel</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={onSave}
            isLoading={isUpdateSparePartTypePending || isCreateSparePartTypePending}
          >
            <Trans id="settings.spare-parts.drawer.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
